<script>
import productCardMixin from '@/v-shop/mixins/product-card-mixin'

export default {
	lang: 'shop',
	mixins: [productCardMixin],

	props: {
		position: String,
	},

	hooksKey: 'ProductCard',
	provide() {
		return {
			hookContainer: () => this,
		}
	},
	cssVars: {
		selector: '.product-card',
	},
}
</script>

<template>
	<!--  VERTICAL CARD -->
	<div class="pb-2 h100" v-if="verticalCard">
		<v-hover v-slot="{ hover }">
			<v-card
				:hover="!outOfStock ? true : false"
				flat
				@click="cardClick"
				@mouseover="firstImageHover = true"
				:ripple="false"
				:class="[
					$bem('product-card-center-aligned-with-timer-vertical'),
					{
						'product-card-center-aligned-with-timer-vertical__positionRight': position == 'right',
						'product-card-center-aligned-with-timer-vertical__positionLeft': position == 'left',
					},
				]"
				class="h100 d-flex flex-column base rounded-md overflow-hidden product-card"
			>
				<hook zone="attrs&financingTags" :class="{ 'no-stock': outOfStock }">
					<div
						v-if="financingTagText"
						:class="[$bem('__financing'), timerProduct ? 'font-0' : 'font-1']"
						class="line-clamp-1 px-4 mt-3 font-weight-bold primary text-center py-1"
					>
						{{ financingTagText }}
					</div>
					<div v-else style="height: 38px"></div>
				</hook>

				<div :class="$bem('__image-cont')" v-if="!outOfStock">
					<hook zone="image">
						<Media :class="$bem('__main-image')" :src="mainImage" width="100%" aspect-ratio="1" />
						<div v-if="secondImage && firstImageHover" :class="$bem('__second-image-cont')">
							<Media
								:src="secondImage"
								width="100%"
								:class="[$bem('__second-image'), hover ? $bem('__second-image--show') : '']"
							/>
						</div>
					</hook>

					<hook zone="offer" v-if="!timerProduct">
						<LimitedTimeOffer :product="product" v-if="hasLimitedTimeOffer" :class="$bem('__offer')" />
					</hook>
				</div>

				<div :class="$bem('__image-cont', 'mx-4 no-stock')" v-else>
					<hook zone="image">
						<Media :class="$bem('__main-image')" :src="mainImage" width="100%" aspect-ratio="1" />
					</hook>

					<hook zone="offer" v-if="!timerProduct">
						<LimitedTimeOffer :product="product" v-if="hasLimitedTimeOffer" :class="$bem('__offer')" />
					</hook>
				</div>

				<hook zone="price">
					<PriceText
						:amount="price"
						:currency="currency"
						:class="[$bem('__price'), { 'no-stock': outOfStock }, timerProduct ? 'font-6' : 'font-7']"
						class="price line-clamp-1 mt-2"
					/>
				</hook>

				<div
					class="d-flex align-center justify-center pb-2 line-clamp-1"
					:class="[$bem('__prev-price'), { 'no-stock': outOfStock }]"
					v-if="prevPrice"
				>
					<hook zone="prevPrice">
						<del class="grey--text">
							<PriceText
								:amount="prevPrice"
								:currency="currency"
								:currencyMargin="0"
								:priceClass="timerProduct ? 'font-2 grey--text' : 'font-3 grey--text'"
							/>
						</del>
					</hook>

					<hook zone="discount">
						<span
							:class="[$bem('__discount'), timerProduct ? 'font-2' : 'font-3']"
							class="d-inline-block discount"
							v-if="discountPct > 0"
						>
							{{ discountPct }}% OFF
						</span>
					</hook>
				</div>

				<router-link class="card-anchor header" :to="productRoute">
					<hook zone="name">
						<div
							:class="[
								$bem('__name'),
								{ 'no-stock': outOfStock, 'mb-4': !timerProduct && !brand },
								timerProduct ? 'font-1' : 'font-2',
							]"
							class="line-clamp-2 px-4 header text-center font-weight-regular"
						>
							{{ product.name }}
						</div>
					</hook>
				</router-link>

				<hook zone="brand" v-if="brand && !timerProduct" :class="{ 'no-stock': outOfStock }">
					<div :class="$bem('__brand')" class="font-0 line-clamp-1 px-4 text-center mb-4">
						{{ brand }}
					</div>
				</hook>

				<hook zone="event-icon" :class="{ 'no-stock': outOfStock }">
					<CollectionEventIcon
						:product="product"
						:class="$b.t ? $bem('__event-icon-tablet') : $bem('__event-icon')"
					/>
				</hook>

				<hook zone="offerTimerProduct" :class="{ 'no-stock': outOfStock }" v-if="timerProduct">
					<div :class="[$bem('__offerTimerProduct'), 'base pt-1 mt-5']">
						<LimitedTimeOfferCountdown :limitedTimeOffer="limitedTimeOffer" timerCard class="mt-3" />
						<div class="mt-2 pb-2">
							<p
								class="text-uppercase line-clamp-3 mb-0 font-weight-medium py-1 px-2 rounded font-0 text-center"
							>
								{{ limitedTimeOfferDate }}
							</p>
						</div>
					</div>
				</hook>
			</v-card>
		</v-hover>
	</div>
	<!-- END OF VERTICAL CARD -->

	<!-- HORIZONTAL CARD FOR MOBILE -->
	<div class="w100" v-else>
		<v-card
			flat
			@click="cardClick"
			:ripple="false"
			:class="[
				$bem('product-card-center-aligned-with-timer-horizontal'),
				{ 'product-card-center-aligned-with-timer-horizontal__noTimer': !timerProduct && $b.t },
			]"
			class="d-flex overflow-hidden base product-card pt-2 flex-column"
		>
			<div class="d-flex overflow-hidden py-2">
				<div
					:class="[
						$bem('__image-cont', 'col col-5 pa-0 d-flex flex-column justify-center'),
						{ 'no-stock': outOfStock },
					]"
				>
					<hook zone="attrs&financingTagsHorizontal" :class="{ 'no-stock': outOfStock }">
						<div style="width: max-content">
							<div
								:class="$bem('__financing')"
								class="font-0 line-clamp-1 px-3 py-1 font-weight-bold primary text-center mb-4"
								v-if="financingTagText"
							>
								{{ financingTagText }}
							</div>
							<div v-else style="height: 34px"></div>
						</div>
					</hook>
					<hook zone="imageHorizontal">
						<Media
							:class="$bem('__main-image', 'px-1')"
							:src="mainImage"
							:width="$b.m ? '100%' : '200px'"
							:maxHeight="$b.t ? '200px' : ''"
							imgClass="product-card-center-aligned-with-timer-horizontal__image--smallWidth"
						/>
					</hook>

					<hook zone="offer" v-if="!timerProduct && $b.t">
						<LimitedTimeOffer :product="product" v-if="hasLimitedTimeOffer" :class="$bem('__offer')" />
					</hook>
				</div>

				<div
					class="col col-7 py-0 pl-1 d-flex flex-column justify-center"
					:class="{ 'no-stock': outOfStock }"
				>
					<hook zone="event-iconHorizontal">
						<CollectionEventIcon
							:product="product"
							:class="[$bem('__event-icon'), 'text-end']"
							:imgWidth="$b.t ? '40px' : '30px'"
						/>
					</hook>
					<hook zone="priceHorizontal">
						<PriceText
							:amount="price"
							:currency="currency"
							:class="[$bem('__price'), $b.m ? 'font-6' : 'font-7']"
							class="price line-clamp-1"
						/>
					</hook>

					<div
						class="d-flex align-center pb-2 line-clamp-1"
						:class="$bem('__prev-price')"
						v-if="prevPrice"
					>
						<hook zone="prevPriceHorizontal">
							<del class="grey--text">
								<PriceText
									:amount="prevPrice"
									:currency="currency"
									:currencyMargin="0"
									:priceClass="$b.m ? 'grey--text font-2' : 'grey--text font-3'"
								/>
							</del>
						</hook>

						<hook zone="discountHorizontal">
							<span
								:class="[$bem('__discount'), $b.m ? 'font-2' : 'font-3']"
								class="d-inline-block discount ml-1"
								v-if="discountPct > 0"
							>
								{{ discountPct }}% OFF
							</span>
						</hook>
					</div>

					<router-link class="card-anchor header" :to="productRoute">
						<hook zone="nameHorizontal">
							<div
								:class="[$bem('__name'), $b.m ? 'font-1' : 'font-2']"
								class="line-clamp-2 header font-weight-regular"
							>
								{{ product.name }}
							</div>
						</hook>
					</router-link>
				</div>
			</div>

			<hook zone="offerHorizontalTimerProduct" :class="{ 'no-stock': outOfStock }" v-if="timerProduct">
				<div :class="[$bem('__offerTimerProduct'), 'base pt-1']">
					<LimitedTimeOfferCountdown :limitedTimeOffer="limitedTimeOffer" timerCard class="mt-2" />
					<div class="mt-2">
						<p
							class="text-uppercase line-clamp-3 mb-0 font-weight-medium pt-1 pb-2 px-2 rounded font-0 text-center"
						>
							{{ limitedTimeOfferDate }}
						</p>
					</div>
				</div>
			</hook>
		</v-card>
	</div>
	<!-- END OF HORIZONTAL CARD FOR MOBILE -->
</template>

<style lang="scss" scoped>
.product-card-center-aligned-with-timer-vertical {
	position: relative;
	cursor: pointer;
	justify-content: space-between;
	// border-bottom: 15px solid var(--secondary);

	// &__timerProduct {
	// 	border-bottom: transparent;
	// }

	&__image-cont {
		position: relative;
	}

	&__second-image-cont {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	&__second-image {
		transition: opacity 0.3s;
		opacity: 0;
		&--show {
			opacity: 1;
		}
	}

	&__event-icon {
		position: absolute;
		top: 50%;
		right: 10px;
		z-index: 1;
	}

	&__event-icon-tablet {
		position: absolute;
		top: 6px;
		right: 6px;
		z-index: 1;
	}

	&__price {
		text-align: center !important;
	}

	&__prev-price {
		column-gap: 8px;
	}

	&__offerTimerProduct {
		border-top: 2px solid var(--offertext);
	}

	&__offer {
		position: absolute;
		bottom: -4px;
	}

	&__discount {
		padding: 2px 5px;
		border-radius: 4px;
	}

	&__positionRight {
		border-top-right-radius: 30px;
		border-bottom-right-radius: 30px;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
		// border-left: 6px solid var(--secondary);
	}

	&__positionLeft {
		border-top-left-radius: 30px;
		border-bottom-left-radius: 30px;
		border-top-right-radius: 0;
		border-bottom-right-radius: 0;
		// border-right: 6px solid var(--secondary);
	}
}

.product-card-center-aligned-with-timer-horizontal {
	// border-left: 4px solid var(--secondary);

	// &__noTimer {
	// 	border-bottom: 15px solid var(--secondary);
	// }

	&__main-image {
		left: 50%;
		transform: translateX(-50%);
	}

	&__offerTimerProduct {
		border-top: 2px solid var(--offertext);
	}

	&__event-icon {
		position: absolute;
		top: 6px;
		right: 3px;
	}

	&__discount {
		padding: 2px 5px;
		border-radius: 4px;
	}
}

.no-stock {
	opacity: 0.4;
}

a.card-anchor:before {
	content: '';
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	z-index: 2;
	left: 0;
}
</style>
